/**
 * Shared support chat helpers — aligned with Flutter ChatMessageController / ChatMessagesScreen:
 * sort by time ascending, sender identity from sender.user_login.ID, sent-cache for optimistic match,
 * grouped bubbles (first vs continuation).
 */

export const CHAT_SENT_CACHE_KEY = "support_chat_recent_sent_messages";

export function normalizeChatText(value) {
  return (value || "")
    .toString()
    .toLowerCase()
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseMessageTime(value) {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

export function persistRecentSentMessages(items) {
  try {
    sessionStorage.setItem(CHAT_SENT_CACHE_KEY, JSON.stringify(items));
  } catch (_) {}
}

export function loadRecentSentCacheIntoRef(recentSentMessagesRef) {
  try {
    const raw = sessionStorage.getItem(CHAT_SENT_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    const now = Date.now();
    recentSentMessagesRef.current = parsed
      .filter((item) => item && typeof item.text === "string" && item.sentAt)
      .filter((item) => now - Number(item.sentAt) <= 48 * 60 * 60 * 1000)
      .slice(-100);
  } catch (_) {}
}

export function enrichMessagesWithSentCache(messages, sentCache, now = Date.now()) {
  const cache = sentCache || [];
  return (messages || []).map((msg) => {
    const text = normalizeChatText(msg?.text);
    if (!text) return { ...msg, clientSentMatch: false };
    const msgTime = parseMessageTime(msg?.createdAt);
    const matchedRecent = cache.some((sent) => {
      const sentText = normalizeChatText(sent?.text);
      if (!sentText || sentText !== text) return false;
      const sentAt = Number(sent.sentAt) || now;
      if (!msgTime) return now - sentAt <= 48 * 60 * 60 * 1000;
      return Math.abs(msgTime - sentAt) <= 48 * 60 * 60 * 1000;
    });
    return { ...msg, clientSentMatch: matchedRecent };
  });
}

export function sortChatMessagesAsc(messages) {
  return [...(messages || [])].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : null;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : null;
    if (aTime && bTime && aTime !== bTime) return aTime - bTime;
    if (aTime && !bTime) return -1;
    if (!aTime && bTime) return 1;
    const aId = Number(a?.id ?? 0);
    const bId = Number(b?.id ?? 0);
    return aId - bId;
  });
}

function messageDedupeKey(m) {
  if (m?.id != null && String(m.id) !== "") return `id:${String(m.id)}`;
  const t = normalizeChatText(m?.text);
  const tm = m?.createdAt ? String(m.createdAt) : "";
  return `tmp:${t}:${tm}:${m?.clientSentMatch ? "1" : "0"}`;
}

/**
 * Merge lists by stable id when present; otherwise by weak key. Output sorted ascending by time.
 */
export function mergeMessagesById(existing, incoming) {
  const map = new Map();
  const add = (m) => {
    map.set(messageDedupeKey(m), m);
  };
  (existing || []).forEach(add);
  (incoming || []).forEach(add);
  return sortChatMessagesAsc([...map.values()]);
}

export function resolveIsMeSender(msg, currentUserIdStr) {
  // Primary: same as Flutter ChatModel.isMeSender — sender.user_login.ID vs logged-in user
  if (currentUserIdStr && msg?.senderUserId != null && String(msg.senderUserId) !== "") {
    return String(msg.senderUserId) === String(currentUserIdStr);
  }
  const fromUserValue = msg?.fromUser;
  if (
    fromUserValue === 1 ||
    fromUserValue === true ||
    fromUserValue === "1" ||
    fromUserValue === "true"
  ) {
    return true;
  }
  if (
    fromUserValue === 0 ||
    fromUserValue === false ||
    fromUserValue === "0" ||
    fromUserValue === "false"
  ) {
    return false;
  }
  if (msg?.clientSentMatch) return true;
  const senderTypeValue = (msg?.senderType || "").toString().toLowerCase();
  if (senderTypeValue.includes("support") || senderTypeValue.includes("admin") || senderTypeValue.includes("agent")) {
    return false;
  }
  if (senderTypeValue.includes("user") || senderTypeValue.includes("client") || senderTypeValue.includes("customer")) {
    return true;
  }
  return false;
}

/**
 * @param {Array} messages
 * @param {string} currentUserIdStr
 * @returns {Array} messages with isMe + firstInSequence (like MessageBubble.first / .next)
 */
export function buildDisplayMessages(messages, currentUserIdStr) {
  const sorted = sortChatMessagesAsc(messages);
  const withMe = sorted.map((msg) => ({
    ...msg,
    isMe: resolveIsMeSender(msg, currentUserIdStr),
  }));
  return withMe.map((msg, i) => {
    const prev = i > 0 ? withMe[i - 1] : null;
    const firstInSequence =
      !prev ||
      msg.isMe !== prev.isMe ||
      String(msg?.senderUserId ?? "") !== String(prev?.senderUserId ?? "");
    return { ...msg, firstInSequence };
  });
}
